import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, from, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { AppConstants } from 'src/app/commons/app-constants';
import { SideBarMenuService } from '../side-bar-menu.service';

const SUPER_USER_ROLES = ['SUPER_ADMIN', 'ROOT'];

/**
 * Global Inventory module visibility. Combines the SUPER_ADMIN/ROOT bypass (they can always
 * see/use Inventory, even while it's OFF for everyone else) with the Config.inventoryModuleEnabled
 * flag, so every consumer (menu-adjacent UI, route guard) shares one visibility decision.
 */
@Injectable({ providedIn: 'root' })
export class InventoryModuleService {
  private readonly visibleSubject = new BehaviorSubject<boolean>(false);
  readonly visible$ = this.visibleSubject.asObservable();

  private loaded = false;

  constructor(private http: HttpClient, private sideBarMenu: SideBarMenuService) {}

  get visible(): boolean {
    return this.visibleSubject.value;
  }

  load(): void {
    if (this.loaded) return;
    this.loaded = true;
    this.checkVisible().subscribe((v) => this.visibleSubject.next(v));
  }

  refresh(): void {
    this.loaded = false;
    this.load();
  }

  setFromBootstrap(visible: boolean): void {
    this.loaded = true;
    this.visibleSubject.next(visible);
  }

  /** Fresh, uncached check — used by the route guard to avoid a cold-start race against the default-false value. */
  checkVisible(): Observable<boolean> {
    return this.isSuperUser$().pipe(
      switchMap((isSuperUser) => {
        if (isSuperUser) return of(true);
        return this.http
          .get<{ inventoryModuleEnabled: boolean }>(`${AppConstants.API_URL}api/v1/app/inventory-module`)
          .pipe(
            map((res) => res.inventoryModuleEnabled),
            catchError(() => of(true)), // network error → allow; matches PermissionGuard's convention
          );
      }),
    );
  }

  /** Public so other SUPER_ADMIN/ROOT-only UI (not just Inventory-module gating) can reuse
   *  this exact role check instead of duplicating it. */
  isSuperUser$(): Observable<boolean> {
    return from(this.getUserProfile()).pipe(
      map((profile: any) => {
        const roles: string[] = profile?.roles || [];
        return roles.some((r) => SUPER_USER_ROLES.includes((r || '').replace(/^ROLE_/, '')));
      }),
      catchError(() => of(false)),
    );
  }

  private async getUserProfile(): Promise<any> {
    const cached = await this.sideBarMenu.getDecryptedUserProfile();
    if (cached) return cached;
    return new Promise((resolve, reject) => {
      this.sideBarMenu.getUserProfile().subscribe({
        next: (profile: any) => resolve(profile),
        error: reject,
      });
    });
  }
}
